import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import useShortcuts from '../../hooks/useShortcuts'
import { AlertDialogShortcutsKeyName } from './ShortcutsKeymap'
import { ShortcutsCategories, ShortcutType } from './Shortcuts.types'
import { RootState } from '../../store'
import { removeUserAlert } from '../../reducer/alerts.reducer'
import { removeAlert } from '../../services/apis/alerts'
import { UserAlert } from '../../proto/generated/nexus'

/**
 * Registers keyboard shortcuts for alert navigation and management
 * These shortcuts work when the alert panel/list is active
 */
const AlertShortcutsRegister = () => {
    const dispatch = useDispatch()
    const userAlerts = useSelector((state: RootState) => state.alertsStore.alerts)
    const alertModalData = useSelector((state: RootState) => state.alertsStore.alertModalData)

    // Track selected/focused alert index
    const [selectedAlertIndex, setSelectedAlertIndex] = useState(0)
    const [selectedAlerts, setSelectedAlerts] = useState<Set<string>>(new Set())

    const handleNextAlert = useCallback((event: KeyboardEvent) => {
        event.preventDefault()
        AnalyticsManager.logEvent('Next Alert', { source: 'keyboard' })
        
        if (userAlerts.length === 0) return
        
        setSelectedAlertIndex((prev) => {
            const next = Math.min(prev + 1, userAlerts.length - 1)
            return next
        })
    }, [userAlerts.length])

    const handlePreviousAlert = useCallback((event: KeyboardEvent) => {
        event.preventDefault()
        AnalyticsManager.logEvent('Previous Alert', { source: 'keyboard' })
        
        if (userAlerts.length === 0) return
        
        setSelectedAlertIndex((prev) => {
            const next = Math.max(prev - 1, 0)
            return next
        })
    }, [userAlerts.length])

    const handleSelectAllAlerts = useCallback((event: KeyboardEvent) => {
        event.preventDefault()
        AnalyticsManager.logEvent('Select All Alerts', { source: 'keyboard' })
        
        const allAlertIds = new Set<string>(userAlerts.map((alert: UserAlert) => alert.id))
        setSelectedAlerts(allAlertIds)
    }, [userAlerts])

    const handleSelectNextAlert = useCallback((event: KeyboardEvent) => {
        event.preventDefault()
        AnalyticsManager.logEvent('Select Next Alert', { source: 'keyboard' })
        
        if (userAlerts.length === 0) return
        
        const nextIndex = Math.min(selectedAlertIndex + 1, userAlerts.length - 1)
        const nextAlert = userAlerts[nextIndex]
        
        if (nextAlert) {
            setSelectedAlerts((prev) => new Set([...prev, nextAlert.id]))
            setSelectedAlertIndex(nextIndex)
        }
    }, [userAlerts, selectedAlertIndex])

    const handleSelectPreviousAlert = useCallback((event: KeyboardEvent) => {
        event.preventDefault()
        AnalyticsManager.logEvent('Select Previous Alert', { source: 'keyboard' })
        
        if (userAlerts.length === 0) return
        
        const prevIndex = Math.max(selectedAlertIndex - 1, 0)
        const prevAlert = userAlerts[prevIndex]
        
        if (prevAlert) {
            setSelectedAlerts((prev) => new Set([...prev, prevAlert.id]))
            setSelectedAlertIndex(prevIndex)
        }
    }, [userAlerts, selectedAlertIndex])

    const handleRemoveAlertNoConfirm = useCallback(async (event: KeyboardEvent) => {
        event.preventDefault()
        AnalyticsManager.logEvent('Remove Alert Without Confirmation', { source: 'keyboard' })
        
        // Remove currently selected alert(s)
        if (selectedAlerts.size > 0) {
            for (const alertId of selectedAlerts) {
                try {
                    await removeAlert({ id: alertId })
                    dispatch(removeUserAlert(alertId))
                } catch (error) {
                    console.error('Failed to remove alert:', alertId, error)
                }
            }
            setSelectedAlerts(new Set())
        } else if (userAlerts[selectedAlertIndex]) {
            // Remove the currently focused alert
            const alertId = userAlerts[selectedAlertIndex].id
            try {
                await removeAlert({ id: alertId })
                dispatch(removeUserAlert(alertId))
            } catch (error) {
                console.error('Failed to remove alert:', alertId, error)
            }
        }
    }, [selectedAlerts, userAlerts, selectedAlertIndex, dispatch])

    const handleSaveAlertChanges = useCallback((event: KeyboardEvent) => {
        event.preventDefault()
        AnalyticsManager.logEvent('Save Alert Changes', { source: 'keyboard' })
        
        // This would trigger the save button in the alert modal
        // The alert modal handles its own save logic
        // This is just for analytics tracking
        console.log('Save alert changes - handled by alert modal')
    }, [])

    const shortcuts: ShortcutType = useMemo(() => {
        const map: ShortcutType = {}

        // Navigation shortcuts
        map[AlertDialogShortcutsKeyName.nextAlert] = handleNextAlert
        map[AlertDialogShortcutsKeyName.previousAlert] = handlePreviousAlert
        map[AlertDialogShortcutsKeyName.nextAlertSpace] = handleNextAlert
        map[AlertDialogShortcutsKeyName.previousAlertSpace] = handlePreviousAlert

        // Selection shortcuts
        map[AlertDialogShortcutsKeyName.selectAllAlerts] = handleSelectAllAlerts
        map[AlertDialogShortcutsKeyName.selectNextAlert] = handleSelectNextAlert
        map[AlertDialogShortcutsKeyName.selectPreviousAlert] = handleSelectPreviousAlert

        // Action shortcuts
        map[AlertDialogShortcutsKeyName.removeAlertNoConfirm] = handleRemoveAlertNoConfirm
        map[AlertDialogShortcutsKeyName.saveAlertChanges] = handleSaveAlertChanges

        return map
    }, [
        handleNextAlert,
        handlePreviousAlert,
        handleSelectAllAlerts,
        handleSelectNextAlert,
        handleSelectPreviousAlert,
        handleRemoveAlertNoConfirm,
        handleSaveAlertChanges
    ])

    useShortcuts(ShortcutsCategories.ALERT_DIALOG, shortcuts)

    return null
}

export default AlertShortcutsRegister
